import clsx from 'clsx';
import { Activity, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  IconEye,
  IconSearch,
  IconFileExport,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import { SegmentedControl } from '@/components/SegmentedControl';
import CustomTable from '@/components/Table/index';
import {
  SubscriptionStatusConfig,
  SUBSCRIPTION_PLAN_OPTIONS,
} from '@/constants/constant';
import { EExportType, ESubscriptionStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getSchoolLicenseReport,
  exportSchoolLicenseReport,
} from '@/service/reportService';
import useCommonStore from '@/store/useCommonStore';
import { ISchoolLicenseItem } from '@/types';
import {
  pickBy,
  downloadFile,
  getYearOptions,
  formatCurrency,
  customPredicate,
  formatLastActive,
  formatUnixToLongDate,
} from '@/utils';

import LicenseFilterModal, {
  ILicenseFilterValues,
} from './components/LicenseFilterModal';
import ParentDetailsModal from './components/ParentDetailsModal';
import RenewLicenseModal from './components/RenewLicenseModal';
import SchoolDetailsModal from './components/SchoolDetailsModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IParentLicenseRecord {
  id: string;
  name: string;
  image: string;
  status: ESubscriptionStatus;
  expiry_date: string;
  revenue: string;
  last_update: string;
}

type TSchoolLicenseParams = {
  search?: string;
  offset?: number;
  limit?: number;
  order?: Record<string, string>;
  subscription_status?: string;
  year?: string;
};

const PAGE_SIZE = 10;

// ─── Mock Data (parents tab only) ────────────────────────────────────────────

const MOCK_PARENT_LICENSES: IParentLicenseRecord[] = [
  {
    id: '1',
    name: 'Fatima Hassan',
    image: 'https://randomuser.me/api/portraits/women/1.jpg',
    status: ESubscriptionStatus.ACTIVE,
    expiry_date: 'May 15, 2025',
    revenue: '$45K',
    last_update: '2h ago',
  },
  {
    id: '2',
    name: 'Mohammed AL-Sayed',
    image: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: ESubscriptionStatus.ACTIVE,
    expiry_date: 'Jun 11, 2025',
    revenue: '$38K',
    last_update: '2h ago',
  },
  {
    id: '3',
    name: 'Sarah Ahmed',
    image: 'https://randomuser.me/api/portraits/women/2.jpg',
    status: ESubscriptionStatus.ACTIVE,
    expiry_date: 'Dec 03, 2025',
    revenue: '$42K',
    last_update: '4h ago',
  },
  {
    id: '4',
    name: 'Aisha Ibrahim',
    image: 'https://randomuser.me/api/portraits/women/3.jpg',
    status: ESubscriptionStatus.ACTIVE,
    expiry_date: 'Oct 18, 2025',
    revenue: '$35K',
    last_update: '4h ago',
  },
  {
    id: '5',
    name: 'Leila Khalil',
    image: 'https://randomuser.me/api/portraits/women/4.jpg',
    status: ESubscriptionStatus.ACTIVE,
    expiry_date: 'May 09, 2025',
    revenue: '$28K',
    last_update: '4h ago',
  },
  {
    id: '6',
    name: 'Zainab Mahmod',
    image: 'https://randomuser.me/api/portraits/women/5.jpg',
    status: ESubscriptionStatus.EXPIRING_SOON,
    expiry_date: 'Dec 03, 2025',
    revenue: '$32K',
    last_update: '4h ago',
  },
];

const License = () => {
  const { formatText } = useFormatText();
  const { loadingTable, setLoadingTable } = useCommonStore();
  const [parentSearch, setParentSearch] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');
  const debouncedParentSearch = useDebounce(parentSearch?.trim(), 500);
  const debouncedSchoolSearch = useDebounce(schoolSearch?.trim(), 500);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    (searchParams.get('tab') as 'parents' | 'schools') ?? 'schools';
  const setActiveTab = (tab: 'parents' | 'schools') =>
    setSearchParams({ tab }, { replace: true });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC' | '';
  }>({ key: '', direction: '' });

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [isOpenRenewModal, setIsOpenRenewModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [filterValues, setFilterValues] = useState<ILicenseFilterValues>({
    status: '',
    plan: '',
    year: '',
  });

  // Schools tab — server-side state
  const [schoolData, setSchoolData] = useState<ISchoolLicenseItem[]>([]);
  const [schoolTotal, setSchoolTotal] = useState(0);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const yearOptions = getYearOptions();

  const fetchSchoolLicenses = async () => {
    setLoadingTable(true);
    const params = pickBy(
      {
        search: debouncedSchoolSearch || undefined,
        status: filterValues.status,
        year: filterValues.year,
        offset: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        order:
          sortConfig?.key && sortConfig?.direction
            ? { [sortConfig.key]: sortConfig.direction }
            : undefined,
      },
      customPredicate
    ) as TSchoolLicenseParams;

    const response = await getSchoolLicenseReport(
      params as Record<string, unknown>
    );
    if (response?.status) {
      setSchoolData(response?.data || []);
      setSchoolTotal(response?.total || 0);
    } else {
      setSchoolData([]);
      setSchoolTotal(0);
    }
    setLoadingTable(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSchoolSearch, filterValues.status, filterValues.year]);

  useEffect(() => {
    if (activeTab !== 'schools') return;
    fetchSchoolLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    debouncedSchoolSearch,
    filterValues.status,
    filterValues.year,
    currentPage,
    sortConfig,
  ]);

  const handleExport = async (docType: EExportType) => {
    if (activeTab !== 'schools') return;
    setIsExportLoading(true);

    try {
      const params = pickBy(
        {
          doc_type: docType,
          search: debouncedSchoolSearch || undefined,
          status: filterValues.status,
          year: filterValues.year,
          offset: (currentPage - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          order:
            sortConfig?.key && sortConfig?.direction
              ? { [sortConfig.key]: sortConfig.direction }
              : undefined,
        },
        customPredicate
      ) as Record<string, unknown>;

      const response = await exportSchoolLicenseReport(params);
      if (response) {
        const ext = docType === EExportType.CSV ? 'csv' : 'pdf';
        const label = formatText('report-analytics.license.title')
          .replace(/\s+/g, '_')
          .toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${ext}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleFilterApply = (values: ILicenseFilterValues) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleViewParent = (id: string) => {
    setSelectedId(id);
    setIsOpenDetailsModal(true);
  };

  const handleNavigateToRenew = () => {
    setIsOpenDetailsModal(false);
    setIsOpenRenewModal(true);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'ASC'
            ? 'DESC'
            : prev.direction === 'DESC'
              ? ''
              : 'ASC'
          : 'ASC',
    }));
  };

  // ─── Parent (mock) columns ─────────────────────────────────────────────────
  const parentColumns = [
    {
      title: 'report-analytics.license.table.parent-name',
      key: 'name',
      width: '240px',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (row: IParentLicenseRecord) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[{ id: row.id, name: row.name, image: row.image }]}
            size={40}
            avatarClassName="w-10 h-10"
          />
          <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
            {row.name}
          </span>
        </div>
      ),
    },
    {
      title: 'report-analytics.license.table.status',
      key: 'status',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IParentLicenseRecord) => {
        const config = SubscriptionStatusConfig[row.status];
        return (
          <span
            className={clsx(
              'px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'report-analytics.license.table.expiry-date',
      key: 'expiry_date',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('expiry_date'),
      render: (row: IParentLicenseRecord) => (
        <span className="text-sm font-medium text-neutral-secondary">
          {row.expiry_date}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.table.revenue',
      key: 'revenue',
      width: '130px',
      sorter: true,
      handleSort: () => handleSort('revenue'),
      render: (row: IParentLicenseRecord) => (
        <span className="bg-success-secondary text-white border-none py-1 px-3 rounded-full text-xs font-bold whitespace-nowrap">
          {row.revenue}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.table.last-update',
      key: 'last_update',
      width: '140px',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: IParentLicenseRecord) => (
        <span className="text-sm font-medium text-neutral-secondary">
          {row.last_update}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.table.action',
      key: 'action',
      width: '80px',
      align: 'center' as const,
      render: (row: IParentLicenseRecord) => (
        <ActionDropdown
          actions={[
            {
              key: 'view',
              label: 'common.actions.view',
              icon: <IconEye size={18} />,
              onClick: () => handleViewParent(row.id),
            },
          ]}
        />
      ),
    },
  ];

  // ─── School (API) columns ──────────────────────────────────────────────────
  const schoolColumns = [
    {
      title: 'report-analytics.license.table.school-name',
      key: 'school_name',
      width: '260px',
      sorter: true,
      handleSort: () => handleSort('school_name'),
      render: (row: ISchoolLicenseItem) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row.id,
                name: row.school_name,
                image: row.school_logo ?? '',
              },
            ]}
            size={40}
            avatarClassName="w-10 h-10"
            isSquare
          />
          <span className="text-sm font-medium text-neutral-primary whitespace-nowrap">
            {row.school_name}
          </span>
        </div>
      ),
    },
    {
      title: 'report-analytics.license.table.seat-count',
      key: 'seat_count',
      width: '130px',
      sorter: true,
      handleSort: () => handleSort('seat_count'),
      render: (row: ISchoolLicenseItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row.seat_count ?? '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.filters.subscription-status',
      key: 'status',
      width: '180px',
      align: 'center' as const,
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: ISchoolLicenseItem) => {
        const config = SubscriptionStatusConfig[row.status];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'report-analytics.license.table.start-date',
      key: 'start_date',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('start_date'),
      render: (row: ISchoolLicenseItem) => (
        <span className="text-sm font-medium text-neutral-secondary whitespace-nowrap">
          {row.start_date ? formatUnixToLongDate(row.start_date) : '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.table.expiry-date',
      key: 'expiry_date',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('expiry_date'),
      render: (row: ISchoolLicenseItem) => (
        <span className="text-sm font-medium text-neutral-secondary whitespace-nowrap">
          {row.expiry_date ? formatUnixToLongDate(row.expiry_date) : '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.license.table.revenue',
      key: 'revenue',
      width: '130px',
      sorter: true,
      handleSort: () => handleSort('revenue'),
      render: (row: ISchoolLicenseItem) =>
        row.revenue !== null && row.currency ? (
          <span className="bg-success-secondary text-white border-none py-1 px-3 rounded-full text-xs font-bold whitespace-nowrap">
            {formatCurrency(row.revenue, row.currency)}
          </span>
        ) : (
          <span className="text-sm font-medium text-neutral-secondary">-</span>
        ),
    },
    {
      title: 'report-analytics.license.table.last-update',
      key: 'last_update',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: ISchoolLicenseItem) => (
        <span className="text-sm font-medium text-neutral-secondary whitespace-nowrap">
          {formatLastActive(row.last_update ?? undefined, formatText)}
        </span>
      ),
    },
  ];

  const isSchools = activeTab === 'schools';
  const isSchoolFilterApplied = !!filterValues.status || !!filterValues.year;
  const isSchoolEmptySearch =
    isSchools &&
    schoolData.length === 0 &&
    !loadingTable &&
    (!!debouncedSchoolSearch || isSchoolFilterApplied);
  const isSchoolEmpty =
    isSchools &&
    schoolData.length === 0 &&
    !loadingTable &&
    !debouncedSchoolSearch &&
    !isSchoolFilterApplied;

  const search = isSchools ? schoolSearch : parentSearch;
  const setSearch = isSchools ? setSchoolSearch : setParentSearch;

  const filteredParentLicenses = debouncedParentSearch
    ? MOCK_PARENT_LICENSES.filter((row) =>
        row.name.toLowerCase().includes(debouncedParentSearch.toLowerCase())
      )
    : MOCK_PARENT_LICENSES;

  const isFilterApplied =
    !!filterValues?.status || !!filterValues?.plan || !!filterValues?.year;

  return (
    <div className="flex flex-col gap-6">
      {/* Search and Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 ps-1">
          <Input
            name="search"
            placeholder={formatText(
              isSchools
                ? 'report-analytics.license.school-search-placeholder'
                : 'report-analytics.license.parent-search-placeholder'
            )}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="min-w-80"
            inputClassName="py-[12px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 border-neutral-light-200',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>

        {isSchools ? (
          <ExportDropdown
            onCsvExport={() => handleExport(EExportType.CSV)}
            onPdfExport={() => handleExport(EExportType.PDF)}
            disabled={isExportLoading}
          />
        ) : (
          <Button
            icon={<IconFileExport size={20} />}
            label={formatText('common.actions.export-data')}
            variant="primary"
            className="px-4 h-12 bg-purple-600 border-purple-600 hover:bg-purple-700 hover:border-purple-700"
          />
        )}
      </div>

      {/* Segmented Control for License type */}
      <div className="w-fit">
        <SegmentedControl
          value={activeTab}
          onChange={(value) => setActiveTab(value as 'parents' | 'schools')}
          options={[
            // {
            //   label: formatText('report-analytics.license.tabs.parents'),
            //   value: 'parents',
            // },
            {
              label: formatText('report-analytics.license.tabs.schools'),
              value: 'schools',
            },
          ]}
        />
      </div>

      {/* Table Section */}
      <div className="flex flex-col gap-4">
        {isSchools ? (
          <CustomTable
            dataSource={schoolData}
            columns={schoolColumns}
            isLoading={loadingTable}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={schoolTotal}
            fieldName={sortConfig.key}
            sortDirection={sortConfig.direction}
            rootClassName="lg:h-[calc(100vh-380px)]"
            isEmpty={isSchoolEmpty}
            isEmptySearch={isSchoolEmptySearch}
            onRowClick={(id) => {
              setSelectedId(id);
              setIsOpenDetailsModal(true);
            }}
          />
        ) : (
          <CustomTable
            dataSource={filteredParentLicenses}
            columns={parentColumns}
            isLoading={false}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={filteredParentLicenses.length}
            fieldName={sortConfig.key}
            sortDirection={sortConfig.direction}
            rootClassName="lg:h-[calc(100vh-380px)]"
          />
        )}
      </div>

      {isFilterModalOpen && (
        <LicenseFilterModal
          setIsOpen={setIsFilterModalOpen}
          initialValues={filterValues}
          onApply={handleFilterApply}
          planOptions={SUBSCRIPTION_PLAN_OPTIONS}
          yearOptions={yearOptions}
          mode={activeTab}
        />
      )}

      <Activity
        mode={
          isOpenDetailsModal && activeTab === 'parents' ? 'visible' : 'hidden'
        }
      >
        <ParentDetailsModal
          setIsOpen={setIsOpenDetailsModal}
          parentId={selectedId}
          onRenewClick={handleNavigateToRenew}
        />
      </Activity>

      <Activity
        mode={
          isOpenDetailsModal && activeTab === 'schools' ? 'visible' : 'hidden'
        }
      >
        <SchoolDetailsModal
          setIsOpen={setIsOpenDetailsModal}
          schoolId={selectedId}
        />
      </Activity>

      <Activity mode={isOpenRenewModal ? 'visible' : 'hidden'}>
        <RenewLicenseModal
          setIsOpen={setIsOpenRenewModal}
          id={selectedId}
          type={activeTab}
          onBack={() => {
            setIsOpenRenewModal(false);
            setIsOpenDetailsModal(true);
          }}
        />
      </Activity>
    </div>
  );
};

export default License;
